import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useMotionValue, animate, type PanInfo } from 'framer-motion';
import type { PageItem } from '../utils/flattenEpisodesToPages';
import EpisodePage from './EpisodePage';

const SWIPE_THRESHOLD_RATIO = 0.25;
const VELOCITY_THRESHOLD = 300;

interface EpisodePageSliderProps {
  sceneId: string;
  pages: PageItem[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  currentPageRef: (el: HTMLDivElement | null) => void;
}

const EpisodePageSlider = ({
  sceneId,
  pages,
  currentIndex,
  onIndexChange,
  currentPageRef
}: EpisodePageSliderProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 360
  );
  const [displayIndex, setDisplayIndex] = useState(currentIndex);
  const swipeCompletedRef = useRef(false);

  const x = useMotionValue(-pageWidth);

  useEffect(() => {
    setDisplayIndex(currentIndex);
  }, [currentIndex]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: displayIndex triggers reset after swipe; ref guards button-initiated changes
  useLayoutEffect(() => {
    if (swipeCompletedRef.current) {
      swipeCompletedRef.current = false;
      x.set(-pageWidth);
    }
  }, [displayIndex, pageWidth, x]);

  const prevPage = displayIndex > 0 ? pages[displayIndex - 1] : null;
  const currentPage = pages[displayIndex] ?? null;
  const nextPage = displayIndex < pages.length - 1 ? pages[displayIndex + 1] : null;

  const canGoPrev = displayIndex > 0;
  const canGoNext = displayIndex < pages.length - 1;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setPageWidth(el.offsetWidth);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const { offset, velocity } = info;
      const threshold = pageWidth * SWIPE_THRESHOLD_RATIO;
      const velocityX = velocity.x;

      let nextIndex = displayIndex;

      if (offset.x < -threshold || velocityX < -VELOCITY_THRESHOLD) {
        if (canGoNext) nextIndex = displayIndex + 1;
      } else if (offset.x > threshold || velocityX > VELOCITY_THRESHOLD) {
        if (canGoPrev) nextIndex = displayIndex - 1;
      }

      if (nextIndex !== displayIndex) {
        const targetX = nextIndex > displayIndex ? -(pageWidth * 2) : 0;
        animate(x, targetX, {
          type: 'spring',
          stiffness: 400,
          damping: 30,
          onComplete: () => {
            swipeCompletedRef.current = true;
            setDisplayIndex(nextIndex);
            onIndexChange(nextIndex);
          }
        });
      } else {
        animate(x, -pageWidth, { type: 'spring', stiffness: 400, damping: 30 });
      }
    },
    [displayIndex, canGoPrev, canGoNext, pageWidth, onIndexChange, x]
  );
  const dragConstraints = {
    left: canGoNext ? -(pageWidth * 2) : -pageWidth,
    right: canGoPrev ? 0 : -pageWidth
  };

  if (pages.length === 0) return null;

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden touch-none">
      <motion.div
        className="flex h-full"
        style={{
          width: pageWidth * 3,
          x,
          willChange: 'transform'
        }}
        drag="x"
        dragConstraints={dragConstraints}
        dragElastic={0}
        dragDirectionLock
        onDragEnd={handleDragEnd}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      >
        <div className="flex-shrink-0" style={{ width: pageWidth, height: '100%' }}>
          {prevPage ? (
            <EpisodePage
              key={prevPage.globalPageIndex}
              sceneId={sceneId}
              episode={prevPage.episode}
              pageIndex={prevPage.pageIndex}
              totalPagesForEpisode={prevPage.totalPagesForEpisode}
            />
          ) : (
            <div className="h-full w-full bg-background-light dark:bg-background-dark" />
          )}
        </div>
        <div
          ref={currentPageRef}
          className="flex-shrink-0"
          style={{ width: pageWidth, height: '100%' }}
        >
          {currentPage ? (
            <EpisodePage
              key={currentPage.globalPageIndex}
              sceneId={sceneId}
              episode={currentPage.episode}
              pageIndex={currentPage.pageIndex}
              totalPagesForEpisode={currentPage.totalPagesForEpisode}
            />
          ) : null}
        </div>
        <div className="flex-shrink-0" style={{ width: pageWidth, height: '100%' }}>
          {nextPage ? (
            <EpisodePage
              key={nextPage.globalPageIndex}
              sceneId={sceneId}
              episode={nextPage.episode}
              pageIndex={nextPage.pageIndex}
              totalPagesForEpisode={nextPage.totalPagesForEpisode}
            />
          ) : (
            <div className="h-full w-full bg-background-light dark:bg-background-dark" />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default EpisodePageSlider;
